import { View, Text, TouchableOpacity, ScrollView, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import SectionTitle from '@/components/General/SectionTitle';
import { useState, useEffect } from 'react';
import { Query } from '@/utils/react-query/Queries';
import { ActivityIndicator } from 'react-native-paper';
import Checkbox from '@/components/General/inputs/Checkbox';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';

const BASIC_FIELDS = [
    { key: 'id', label: 'ID' },
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'dateOfBirth', label: 'Date of Birth' },
    { key: 'age', label: 'Age' },
    { key: 'gender', label: 'Gender' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'street', label: 'Street' },
    { key: 'streetLine2', label: 'Street Line 2' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'zip', label: 'ZIP' },
    { key: 'lastVisit', label: 'Last Visit' },
];

const ExportFieldsModal = ({ isOpen, onClose, onExport, title = "Export to CSV", checkinFields = [], exportCount }) => {
    // Custom-field export options are paid-only — skip the metadata query and
    // hide the section entirely on Free.
    const { entitlements } = useOrganizationEntitlements();
    const isFree = isFreeOrg(entitlements);
    const { data: customFields, isLoading } = Query.personCustomFields.all({ options: { enabled: !isFree } });
    
    const [selectedFields, setSelectedFields] = useState(() => {
        const initial = {};
        BASIC_FIELDS.forEach(f => { initial[f.key] = true; });
        // Preselect the first three check-in detail fields
        checkinFields.forEach((f, i) => { initial[f.key] = i < 3; });
        return initial;
    });

    useEffect(() => {
        if (customFields && customFields.length > 0) {
            setSelectedFields(prev => {
                const updated = { ...prev };
                customFields.sort((a, b) => a.name.localeCompare(b.name)).forEach(cf => {
                    if (updated[`custom_${cf.id}`] === undefined) {
                        updated[`custom_${cf.id}`] = false;
                    }
                });
                return updated;
            });
        }
    }, [customFields]);

    const toggleField = (key) => {
        setSelectedFields(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const selectAll = () => {
        const allSelected = {};
        BASIC_FIELDS.forEach(f => { allSelected[f.key] = true; });
        customFields?.forEach(cf => { allSelected[`custom_${cf.id}`] = true; });
        checkinFields.forEach(f => { allSelected[f.key] = true; });
        setSelectedFields(allSelected);
    };

    const selectNone = () => {
        const noneSelected = {};
        BASIC_FIELDS.forEach(f => { noneSelected[f.key] = false; });
        customFields?.forEach(cf => { noneSelected[`custom_${cf.id}`] = false; });
        checkinFields.forEach(f => { noneSelected[f.key] = false; });
        setSelectedFields(noneSelected);
    };

    const handleExport = () => {
        const basicSelected = BASIC_FIELDS
            .filter(f => selectedFields[f.key] && (!isFree || f.key !== 'lastVisit'))
            .map(f => f.key);
        
        const customSelected = customFields
            ?.filter(cf => selectedFields[`custom_${cf.id}`])
            .map(cf => ({ id: cf.id, name: cf.name })) || [];

        const checkinSelected = checkinFields
            .filter(f => selectedFields[f.key])
            .map(f => f.key);

        onExport({ basicFields: basicSelected, customFields: customSelected, checkinFields: checkinSelected });
        onClose();
    };

    const selectedCount = Object.values(selectedFields).filter(Boolean).length;
    const totalCount = BASIC_FIELDS.length + (customFields?.length || 0) + checkinFields.length;

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            cancelText="Cancel"
            confirmText="Export"
            onConfirm={handleExport}
        >
            <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled"
>
                <View style={styles.header}>
                    <Text style={styles.selectedText}>
                        {selectedCount} of {totalCount} fields selected{exportCount != null ? ` · ${exportCount} ${exportCount === 1 ? 'person' : 'people'} to export` : ''}
                    </Text>
                    <View style={styles.quickActions}>
                        <TouchableOpacity onPress={selectAll} style={styles.quickAction}>
                            <Text style={styles.quickActionText}>Select All</Text>
                        </TouchableOpacity>
                        <Text style={styles.divider}>|</Text>
                        <TouchableOpacity onPress={selectNone} style={styles.quickAction}>
                            <Text style={styles.quickActionText}>Select None</Text>
                        </TouchableOpacity>
                    </View>
                </View>

                {checkinFields.length > 0 && (
                    <View style={styles.section}>
                        <SectionTitle title="Check In Details" marginLeft={0} marginTop={0} />
                        {checkinFields.map((field) => (
                            <Pressable
                                key={field.key}
                                style={styles.fieldRow}
                                onPress={() => toggleField(field.key)}
                            >
                                <Checkbox
                                    checked={selectedFields[field.key]}
                                    onChange={() => toggleField(field.key)}
                                    size={18}
                                />
                                <Text style={styles.fieldLabel}>{field.label}</Text>
                            </Pressable>
                        ))}
                    </View>
                )}

                <View style={styles.section}>
                    <SectionTitle title="Basic Fields" marginLeft={0} marginTop={0} />
                    {/* lastVisit is attendance-derived — paid-only */}
                    {BASIC_FIELDS.filter(f => !isFree || f.key !== 'lastVisit').map((field) => (
                        <Pressable
                            key={field.key}
                            style={styles.fieldRow}
                            onPress={() => toggleField(field.key)}
                        >
                            <Checkbox
                                checked={selectedFields[field.key]}
                                onChange={() => toggleField(field.key)}
                                size={18}
                            />
                            <Text style={styles.fieldLabel}>{field.label}</Text>
                        </Pressable>
                    ))}
                </View>

                {!isFree && (
                <View style={styles.section}>
                    <SectionTitle title="Custom Fields" marginLeft={0} marginTop={0} />
                    {isLoading ? (
                        <View style={styles.loadingContainer}>
                            <ActivityIndicator size="small" />
                            <Text style={styles.loadingText}>Loading custom fields...</Text>
                        </View>
                    ) : customFields?.length > 0 ? (
                        customFields.map((field) => (
                            <Pressable
                                key={field.id}
                                style={styles.fieldRow}
                                onPress={() => toggleField(`custom_${field.id}`)}
                            >
                                <Checkbox
                                    checked={selectedFields[`custom_${field.id}`]}
                                    onChange={() => toggleField(`custom_${field.id}`)}
                                    size={18}
                                />
                                <Text style={styles.fieldLabel}>{field.name}</Text>
                            </Pressable>
                        ))
                    ) : (
                        <Text style={styles.emptyText}>No custom fields configured</Text>
                    )}
                </View>
                )}
            </ScrollView>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
        paddingBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    selectedText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    quickActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    quickAction: {
        paddingVertical: 4,
        paddingHorizontal: 8,
    },
    quickActionText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.primary,
    },
    divider: {
        color: colors.border,
    },
    section: {
        marginBottom: 20,
    },
    fieldRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 10,
        paddingHorizontal: 12,
        backgroundColor: colors.surface,
        borderRadius: 8,
        marginBottom: 6,
    },
    fieldLabel: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.text,
        flex: 1,
        paddingLeft:10
    },
    loadingContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 20,
        gap: 10,
    },
    loadingText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    emptyText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
        paddingVertical: 16,
    },
}));

export default ExportFieldsModal;
